import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import JourneyScene, { avatarFor } from "../../components/crossroads/JourneyScene";
import useLiveScene from "../../components/crossroads/useLiveScene";
import { CrossroadsPopup, FeedbackPopup, HowToPlayPopup, LearningContent, Spinner, StatTile } from "../../components/crossroads/CrossroadsUi";
import { mergeReportId } from "../loginSlice";
import { completeJourney, getGameContent, joinLiveRun, saveFeedback, saveResponses, sessionToken } from "../../functions/crossroadsApi";
import { extractReportIdFromResponse, resolveReportsAuthToken, sendReport } from "../../functions/sendReport";
import {
  isChoiceQuestion,
  pathColorsFor,
  pickMessage,
  resolveCrossroadsTheme,
  seededShuffle,
  themeCssVars,
} from "../../utils/crossroadsTheme";
import {
  castVote,
  closeVotingIfExpired,
  countVotes,
  isOnline,
  joinLive,
  onlineCount,
  subscribeLiveState,
  subscribeParticipants,
  subscribeRunVotes,
  subscribeServerOffset,
  liveChannelFor,
  voteKey,
  sanitizeFirebaseKey,
} from "../../utils/liveFirebase";
import { isUserDemoBypass } from "../../utils/demoGate";
import useHeaderOffset, { useBoardPageBody } from "../../utils/useHeaderOffset";
import "./Vote.css";

const REVEALED_PHASES = ["reveal", "learning", "complete"];
const QUESTION_PHASES = ["voting", "closed", "reveal", "learning"];
const WIDE_QUERY = "(min-width: 761px)";

function useIsWide() {
  const [wide, setWide] = useState(() => window.matchMedia?.(WIDE_QUERY).matches ?? true);
  useEffect(() => {
    const mq = window.matchMedia?.(WIDE_QUERY);
    if (!mq) return undefined;
    const onChange = () => setWide(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return wide;
}

/**
 * Live Audience player screen (PRD 2.2 / 7.7). Players log in through their normal game
 * link; the admin runs the game from Admin → Live Control and every step appears here.
 */
export default function Vote() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth?.user);

  const token = user ? sessionToken(user.sessionId, user.organizationId) : null;
  // The player's own session channel: only this session's host drives this screen.
  const fbId = user ? liveChannelFor(user.organizationId, user.sessionId) : null;
  const pid = user ? sanitizeFirebaseKey(user.userId) : null;

  const [content, setContent] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [live, setLive] = useState(undefined);
  const [participants, setParticipants] = useState({});
  const [runVotes, setRunVotes] = useState({});
  const [offset, setOffset] = useState(0);
  const [, setTick] = useState(0);
  const [voting, setVoting] = useState(false);
  const [voteError, setVoteError] = useState("");
  const [howto, setHowto] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const reportedRef = useRef("");
  // Bumped to retry reporting a finished game (e.g. the host's results are still being saved).
  const [reportRetry, setReportRetry] = useState(0);
  const wide = useIsWide();
  const headerOffset = useHeaderOffset();
  useBoardPageBody();

  useEffect(() => {
    if (!token) {
      setLoadError("Please open Crossroads from your game link.");
      return;
    }
    getGameContent({ token })
      .then(setContent)
      .catch((err) => setLoadError(err.message || "Failed to load the game."));
  }, [token]);

  // Online while this screen is open; leaving it (e.g. to the leaderboard) takes the player off.
  useEffect(() => {
    if (!fbId || !pid) return undefined;
    return joinLive(fbId, pid, { name: user.name || "Player", userId: user.userId });
  }, [fbId, pid, user?.name, user?.userId]);

  useEffect(() => subscribeLiveState(fbId, setLive), [fbId]);

  // Record that this player is in the current run (participation + completion time), even if
  // they never vote. Once per run; retried if the request fails.
  const joinedRunRef = useRef("");
  useEffect(() => {
    const runId = live?.runId;
    if (!runId || !user || !token || joinedRunRef.current === runId) return undefined;
    joinedRunRef.current = runId;
    let cancelled = false;
    const join = (attempt) =>
      joinLiveRun({ token, runId, userId: user.userId, name: user.name, email: user.email, userType: user.role || user.source, reportId: user.reportId }).catch(() => {
        if (!cancelled && attempt < 5) window.setTimeout(() => join(attempt + 1), 2000 * attempt);
      });
    join(1);
    return () => {
      cancelled = true;
    };
  }, [live?.runId, user, token]);
  useEffect(() => subscribeParticipants(fbId, setParticipants), [fbId]);
  useEffect(() => subscribeServerOffset(setOffset), []);
  useEffect(() => subscribeRunVotes(fbId, live?.runId, setRunVotes), [fbId, live?.runId]);

  const phase = live?.phase || "lobby";
  useEffect(() => {
    if (phase !== "voting") return undefined;
    const t = window.setInterval(() => setTick((n) => n + 1), 250);
    return () => window.clearInterval(t);
  }, [phase]);

  const theme = useMemo(() => resolveCrossroadsTheme(content?.settings), [content]);
  const questions = useMemo(() => {
    const list = content?.questions || [];
    return theme.shuffle && live?.runId ? seededShuffle(list, `live-${live.runId}`) : list;
  }, [content, theme.shuffle, live?.runId]);
  const total = questions.length;
  const index = live?.index ?? 0;
  // Drive the journey animation from the admin's phase changes (same hook as the presenter view).
  const { scenePhase, checkpointKey, sceneIndex, onPhaseDone } = useLiveScene(live, phase, index);
  const question = QUESTION_PHASES.includes(phase)
    ? questions[index] || null
    : null;
  const sceneQuestion = questions[sceneIndex] || null;
  const onBoard = phase !== "lobby" && phase !== "intro";
  const upcoming = questions[sceneIndex + 1];
  const nextCrossroads = upcoming
    ? {
        label: `Scenario ${sceneIndex + 2}`,
        text: upcoming.question_name,
        optionA: upcoming.option_one,
        optionB: upcoming.option_two,
        pathColors: pathColorsFor(upcoming, theme),
      }
    : null;
  const colors = sceneQuestion ? pathColorsFor(sceneQuestion, theme) : { a: theme.colors.pathA, b: theme.colors.pathB };
  const myVote = question && pid ? runVotes?.[voteKey(index)]?.[pid] || null : null;
  // Voting (and its countdown) starts once the crossroads has arrived on screen — not while the
  // board is still walking to it.
  const votingStarted = phase === "voting" && Date.now() + offset >= (live?.votingStartsAt || 0);
  const remainingMs = phase === "voting" && live?.votingEndsAt ? Math.max(0, live.votingEndsAt - (Date.now() + offset)) : 0;
  const votingOpen = votingStarted && remainingMs > 0;

  // The deadline closes voting even if the host's screen isn't connected.
  useEffect(() => {
    if (phase !== "voting" || !live?.votingEndsAt || remainingMs > 0) return;
    closeVotingIfExpired(fbId, { runId: live.runId, index: live.index ?? 0, votingEndsAt: live.votingEndsAt }, Date.now() + offset).catch(
      (err) => console.warn("Could not close voting:", err?.message || err)
    );
  }, [phase, live?.votingEndsAt, live?.runId, live?.index, remainingMs, fbId, offset]);
  const counts = countVotes(runVotes[voteKey(index)]);
  const online = onlineCount(participants);
  // Answers + learning content arrive from the host per crossroads, only once revealed.
  const reveals = live?.reveals || {};
  const revealOf = (i) => reveals[voteKey(i)] || null;


  // Personal tally from the run's votes (survives a refresh), counting revealed crossroads only.
  const tally = useMemo(() => {
    let points = 0;
    let correct = 0;
    let answered = 0;
    questions.forEach((q, i) => {
      const shown = live?.reveals?.[voteKey(i)];
      const revealed = Boolean(shown) && (phase === "complete" || i < index || (i === index && REVEALED_PHASES.includes(phase)));
      const v = runVotes?.[voteKey(i)]?.[pid];
      const right = isChoiceQuestion(q) ? null : shown?.correct || null;
      // Choice-based crossroads have no right answer: not scored, not in accuracy.
      if (!revealed || !v || !right) return;
      answered += 1;
      if (v === right) {
        correct += 1;
        points += Number(q.points) || 0;
      }
    });
    return { points, correct, answered, incorrect: answered - correct, accuracy: answered ? Math.round((correct / answered) * 100) : 0 };
  }, [questions, runVotes, pid, index, phase, live?.reveals]);

  const myResponses = useMemo(
    () =>
      questions
        .map((q, i) => ({ questionId: q.id, questionIndex: i, selected: runVotes?.[voteKey(i)]?.[pid] }))
        .filter((r) => r.selected),
    [questions, runVotes, pid]
  );

  // Report the finished live game once (backend + platform report).
  useEffect(() => {
    if (phase !== "complete" || !user || !live?.runId || reportedRef.current === live.runId) return;
    const flag = `cr_live_reported_${live.runId}_${user.userId}`;
    if (sessionStorage.getItem(flag)) {
      reportedRef.current = live.runId;
      return;
    }
    reportedRef.current = live.runId;
    (async () => {
      try {
        if (myResponses.length) {
          await saveResponses({ token, userId: user.userId, name: user.name, email: user.email, mode: "live", runId: live.runId, responses: myResponses });
        }
        const server = await completeJourney({
          token,
          userId: user.userId,
          name: user.name,
          email: user.email,
          mode: "live",
          runId: live.runId, // the server measures the time from when this player joined the run
          userType: user.role || user.source,
          reportId: user.reportId,
        });
        sessionStorage.setItem(flag, "1");
        if (!isUserDemoBypass(user) && !server.alreadyCompleted) {
          const role = String(user.role || user.source || "ORG_USER").toUpperCase();
          const res = await sendReport({
            token: resolveReportsAuthToken(user.token, token),
            reportId: user.reportId || null,
            userId: user.userId,
            name: user.name,
            email: user.email || null,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
            gameId: user.gameId,
            role,
            user_type: role,
            owner: role,
            points: server.reportedPoints,
            accumulatePoints: false,
            time: 0,
            ans: JSON.stringify({ correct: server.correct, incorrect: server.incorrect, accuracy: server.accuracy, mode: "live" }),
          });
          const rid = extractReportIdFromResponse(res);
          if (rid) dispatch(mergeReportId(rid));
        }
      } catch (err) {
        reportedRef.current = "";
        console.warn("Live result not reported — retrying:", err?.message || err);
        if (reportRetry < 20) window.setTimeout(() => setReportRetry((n) => n + 1), 5000);
      }
    })();
  }, [phase, live?.runId, user, token, myResponses, dispatch, reportRetry]);

  const vote = async (choice) => {
    if (!votingOpen || myVote || voting || !question || !live?.runId) return;
    setVoting(true);
    setVoteError("");
    try {
      const { accepted } = await castVote(fbId, live.runId, voteKey(index), pid, choice);
      if (accepted) {
        // The reports count votes from this record, so retry a dropped request (the whole run's
        // votes are sent again when the game finishes as well).
        const record = (attempt) =>
          saveResponses({
            token,
            userId: user.userId,
            name: user.name,
            email: user.email,
            mode: "live",
            runId: live.runId,
            responses: [{ questionId: question.id, questionIndex: index, selected: choice }],
          }).catch(() => {
            if (attempt < 3) window.setTimeout(() => record(attempt + 1), 1500 * attempt);
          });
        record(1);
      }
    } catch {
      setVoteError("Your vote didn't go through — please try again.");
    } finally {
      setVoting(false);
    }
  };

  // ---- Render ----
  const rootStyle = { ...themeCssVars(theme), "--cr-top": `${headerOffset}px` };
  const showSplit = ["closed", "reveal", "learning"].includes(phase);
  const split = showSplit ? { a: counts.a, b: counts.b } : null;
  // Everyone online, current player first, as avatars in the start panel.
  const sceneAvatars = [
    avatarFor(pid, user?.name),
    ...Object.entries(participants)
      .filter(([id, p]) => isOnline(p) && id !== pid)
      .map(([id, p]) => avatarFor(id, p.name)),
  ];

  const page = ({ popup = null, status = null, cover = null } = {}) => (
    <div className={`cr-root cr-vote${wide ? "" : " is-narrow"}`} style={rootStyle}>
      <div className="cr-vote-stage">
        {content ? (
          <JourneyScene
            phase={scenePhase}
            checkpointKey={checkpointKey}
            pathColors={colors}
            optionA={sceneQuestion?.option_one}
            optionB={sceneQuestion?.option_two}
            scenarioLabel={sceneQuestion && onBoard ? `Scenario ${sceneIndex + 1}` : ""}
            scenarioText={onBoard ? sceneQuestion?.question_name : ""}
            next={nextCrossroads}
            destinationNext={phase === "complete" || sceneIndex + 1 >= total}
            idleTitle={phase === "intro" ? theme.introTitle : theme.title}
            idleText={phase === "intro" ? "" : "Waiting for the host to start"}
            showText
            backgroundUrl={theme.images.journeyDesktop}
            backgroundUrlPortrait={theme.images.journeyPortrait}
            avatars={sceneAvatars}
            selected={split ? null : myVote}
            split={split}
            votes={runVotes?.[voteKey(sceneIndex)] || {}}
            reveal={phase === "reveal" || phase === "learning" ? revealOf(sceneIndex)?.correct || null : null}
            interactive={votingOpen && !myVote && scenePhase === "decide"}
            onChoose={vote}
            onPhaseDone={onPhaseDone}
            insetTop={headerOffset + (wide ? 0 : 34)}
            insetBottom={64}
            timerEndsAt={votingOpen ? live.votingEndsAt - offset : 0}
            timerTotal={theme.votingSeconds * 1000}
            timerSeconds={Math.ceil(remainingMs / 1000)}
          />
        ) : null}
      </div>
      {content && phase !== "lobby" ? (
        <div className="cr-vote-hud">
          {total && QUESTION_PHASES.includes(phase) ? (
            <span className="cr-hud-pill">
              {index + 1} / {total}
            </span>
          ) : null}
          {theme.hasCorrectAnswers ? <span className="cr-hud-pill">★ {tally.points}</span> : null}
        </div>
      ) : null}
      {cover ? <div className="cr-vote-cover">{cover}</div> : null}
      {!popup && status ? (
        <div className="cr-vote-status" role="status">
          {status}
        </div>
      ) : null}
      {popup}
      <HowToPlayPopup open={howto} onClose={() => setHowto(false)} theme={theme} />
    </div>
  );

  if (loadError) return page({ cover: <p className="cr-error">{loadError}</p> });
  if (!content || live === undefined) return page({ cover: <Spinner label="Joining the live game" /> });

  if (!live || phase === "lobby" || phase === "intro") {
    return page({
      popup: (
        <CrossroadsPopup
          open
          theme={theme}
          tone="neutral"
          eyebrow={phase === "intro" ? "Starting now" : "You're in!"}
          title={phase === "intro" ? theme.introTitle : `Hi ${user.name || "there"} 👋`}
          labelledBy="cr-vote-lobby-title"
          backgroundUrl={theme.images.popup}
          actions={
            <>
              <button type="button" className="cr-btn-ghost" onClick={() => setHowto(true)}>
                How to Play
              </button>
            </>
          }
        >
          <p className="cr-vote-pre">
            {phase === "intro"
              ? theme.introText
              : "Waiting for the host to start the game. Keep this screen open — the first crossroads will appear here."}
          </p>
          <p className="cr-vote-count">
            <strong>{online}</strong> player{online === 1 ? "" : "s"} waiting
          </p>
        </CrossroadsPopup>
      ),
      status: <span>Waiting for the host…</span>,
    });
  }

  if (phase === "complete") {
    if (feedbackOpen) {
      return page({
        popup: (
          <FeedbackPopup
            open
            theme={theme}
            onSubmit={(feedback) => saveFeedback({ token, userId: user.userId, feedback })}
            onDone={() => navigate("/leaderboard", { replace: true })}
          />
        ),
      });
    }
    return page({
      popup: (
        <CrossroadsPopup
          open
          theme={theme}
          tone="success"
          eyebrow="🏁 Destination reached"
          title={theme.completionTitle}
          labelledBy="cr-vote-complete-title"
          backgroundUrl={theme.images.popup}
          actions={
            <button type="button" className="cr-btn" onClick={() => setFeedbackOpen(true)}>
              OK
            </button>
          }
        >
          {theme.completionMessage ? <p className="cr-vote-pre">{theme.completionMessage}</p> : null}
          <div className="cr-stats">
            {theme.hasCorrectAnswers ? (
              <>
                <StatTile label="Your score" value={tally.points} />
                <StatTile label="Correct" value={tally.correct} />
                <StatTile label="Incorrect" value={tally.incorrect} />
                <StatTile label="Accuracy" value={`${tally.accuracy}%`} />
              </>
            ) : (
              <StatTile label="Crossroads answered" value={myResponses.length} />
            )}
          </div>
        </CrossroadsPopup>
      ),
      status: <span>Journey complete</span>,
    });
  }

  if (!question) {
    return page({ status: <span>On the road… the next crossroads appears when the host releases it.</span> });
  }

  if (phase === "voting" || phase === "closed") {
    let message = "";
    if (phase === "closed") message = myVote ? "Vote locked in — waiting for the answer" : "Voting closed — waiting for the answer";
    else if (myVote) message = "✓ Your vote is locked in";
    else if (votingOpen) message = `Choose your path — ${wide ? "click" : "tap"} a card or road`;
    else message = "You didn't vote on this crossroads";
    return page({
      status: (
        <>
          {/* the countdown runs around the scenario box on the board */}
          <span className="cr-vote-status-text">{voteError || message}</span>
        </>
      ),
    });
  }

  // reveal / learning
  const isChoice = isChoiceQuestion(question);
  const shownAnswer = revealOf(index);
  const isCorrect = !isChoice && myVote && myVote === shownAnswer?.correct;
  const correctText = shownAnswer?.correct === "A" ? question.option_one : question.option_two;
  const myChoiceText = myVote === "A" ? question.option_one : myVote === "B" ? question.option_two : "";
  let resultLine = "You didn't vote this time";
  if (myVote && isChoice) resultLine = `You chose: ${myChoiceText}`;
  else if (myVote) {
    resultLine = isCorrect
      ? pickMessage(theme.successMessages, `${question.id}-ok`)
      : pickMessage(theme.incorrectMessages, `${question.id}-no`);
  }
  let tone = "neutral";
  if (myVote && !isChoice) tone = isCorrect ? "success" : "fail";
  const splitLine = counts.total
    ? `${Math.round((counts.a / counts.total) * 100)}% chose A · ${Math.round((counts.b / counts.total) * 100)}% chose B`
    : "";

  if (phase === "reveal") {
    return page({
      popup: (
        <CrossroadsPopup
          open
          theme={theme}
          tone={tone}
          eyebrow={`Crossroads ${index + 1} of ${total}`}
          title={resultLine}
          labelledBy="cr-vote-reveal-title"
          backgroundUrl={theme.images.popup}
        >
          {isChoice ? (
            <p>There's no single right road here — see how everyone chose.</p>
          ) : isCorrect ? (
            <span className="cr-points-chip">+{Number(question.points) || 0} points</span>
          ) : (
            <p>
              The better road was <strong>{correctText}</strong>.
            </p>
          )}
          {splitLine ? <p className="cr-vote-split">{splitLine}</p> : null}
        </CrossroadsPopup>
      ),
      status: <span>{splitLine || "Answer revealed"}</span>,
    });
  }

  return page({
    popup: (
      <CrossroadsPopup
        open
        theme={theme}
        tone="learning"
        eyebrow="💡 Learning moment"
        title={shownAnswer?.dyk_heading || theme.dykHeading}
        labelledBy="cr-vote-learning-title"
        backgroundUrl={theme.images.popup}
      >
        {shownAnswer ? <LearningContent question={shownAnswer} /> : null}
      </CrossroadsPopup>
    ),
    status: <span>Learning moment</span>,
  });
}
